import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import Flex from 'components/common/Flex';
import Pagination from 'components/pagination';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useEffect, useState } from 'react';
import { Button, Card, Col, Dropdown, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import CustomReportService from 'services/CustomReport.service';
import CustomReportLayout from './CustomReportLayout';

const CustomReportExecution = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [executionResult, setExecutionResult] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [filters, setFilters] = useState({
    page: 0,
    size: 20
  });

  useEffect(() => {
    loadReportAndExecute();
  }, [uuid, filters.page, filters.size]);

  const loadReportAndExecute = async () => {
    setIsLoading(true);
    try {
      // First load report details
      const reportResponse = await CustomReportService.findByUuid(uuid);
      if (reportResponse && !reportResponse.ok) {
        setReport(reportResponse);

        // Then execute the report with pagination
        const executionResponse = await CustomReportService.execute(
          uuid,
          filters.page,
          filters.size
        );
        if (executionResponse && !executionResponse.ok) {
          setExecutionResult(executionResponse);
        } else {
          toast.error('Error executing report');
        }
      } else {
        toast.error('Error loading report');
        navigate('/reports/custom');
      }
    } catch (error) {
      toast.error('Error loading report');
      navigate('/reports/custom');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async (format = 'xlsx') => {
    setIsExporting(true);
    try {
      const response = await CustomReportService.export(uuid, format);
      if (response && response.ok) {
        // Handle file download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${report.name.replace(/\s+/g, '_')}_${
          new Date().toISOString().split('T')[0]
        }.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Relatório exportado com sucesso');
      } else {
        toast.error('Erro ao exportar relatório');
      }
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    } finally {
      setIsExporting(false);
    }
  };

  const formatCellValue = (value, dataType) => {
    if (value === null || value === undefined) {
      return '-';
    }

    if (dataType === 'datetime' && value) {
      const date = new Date(value);
      return (
        date.toLocaleDateString() +
        ' ' +
        date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      );
    }

    if (dataType === 'number') {
      return value.toLocaleString();
    }

    if (dataType === 'boolean') {
      return value === true || value === 'true' ? 'Sim' : 'Não';
    }

    if (dataType === 'duration') {
      // Assumindo que o valor vem em horas
      const hours = parseFloat(value);
      if (isNaN(hours)) return '-';

      const wholeHours = Math.floor(hours);
      const minutes = Math.round((hours - wholeHours) * 60);

      if (wholeHours === 0) {
        return `${minutes}min`;
      } else if (minutes === 0) {
        return `${wholeHours}h`;
      } else {
        return `${wholeHours}h ${minutes}min`;
      }
    }

    return String(value);
  };

  const buildColumns = () => {
    if (!executionResult || !executionResult.columns) {
      return [];
    }

    return executionResult.columns.map(col => ({
      accessorKey: col.displayName, // Usar displayName como accessorKey pois os dados agora vêm com displayName como chave
      header: col.displayName,
      meta: {
        headerProps: {
          style: { minWidth: '150px' }
        }
      },
      cell: ({ getValue }) => {
        const value = getValue();
        return formatCellValue(value, col.dataType);
      }
    }));
  };

  // Sempre chamar hooks antes dos returns condicionais
  const columns = buildColumns();
  const data = executionResult?.content || [];

  // Debug: verificar estrutura dos dados
  if (executionResult && data.length > 0) {
    console.log('Execution Result:', executionResult);
    console.log('Columns:', columns);
    console.log('Data sample:', data[0]);
  }

  const table = useAdvanceTable({
    columns,
    data
  });

  if (isLoading) {
    return (
      <CustomReportLayout>
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </Spinner>
          <p className="mt-3 text-muted">Executando relatório, aguarde...</p>
        </div>
      </CustomReportLayout>
    );
  }

  if (!report || !executionResult) {
    return null;
  }

  return (
    <CustomReportLayout>
      {/* Header Section */}
      <Flex justifyContent="between" alignItems="center" className="mb-2">
        <Flex alignItems="center">
          <Button
            variant="link"
            className="text-decoration-none text-body me-2 p-0"
            size="sm"
            onClick={() => navigate(`/reports/custom/${uuid}`)}
            title="Voltar para os detalhes"
          >
            <FontAwesomeIcon icon="arrow-left" />
          </Button>
          <Flex>
            <h3 className="m-0">{report.name}</h3>
          </Flex>
        </Flex>
        <Flex>
          <Dropdown className="me-2">
            <Dropdown.Toggle variant="primary" size="sm" disabled={isExporting}>
              {isExporting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Exportando...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon="download" className="me-1" />
                  Exportar
                </>
              )}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handleExport('xlsx')}>
                Excel (.xlsx)
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleExport('pdf')}>
                PDF (.pdf)
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleExport('csv')}>
                CSV (.csv)
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Flex>
      </Flex>

      {/* Execution Metadata */}
      <Row className="g-3 mb-3">
        <Col xs={12}>
          <Card className="shadow-sm">
            <Card.Body className="p-4">
              <Card.Title className="mb-4">Informações da Execução</Card.Title>
              <Row>
                <Col xs={12} sm={6} md={3} className="mb-3">
                  <p className="text-muted mb-1">Total de Registros</p>
                  <h3 className="m-0 text-primary fw-semibold">
                    {executionResult.totalElements.toLocaleString()}
                  </h3>
                </Col>
                <Col xs={12} sm={6} md={3} className="mb-3">
                  <p className="text-muted mb-1">Colunas</p>
                  <h3 className="m-0 text-info fw-semibold">
                    {columns.length}
                  </h3>
                </Col>
                <Col xs={12} sm={6} md={3} className="mb-3">
                  <p className="text-muted mb-1">Tempo de Execução</p>
                  <h3 className="m-0 text-success fw-semibold">
                    {executionResult.metadata?.executionTimeMs || 0}ms
                  </h3>
                </Col>
                <Col xs={12} sm={6} md={3} className="mb-3">
                  <p className="text-muted mb-1">Filtros Aplicados</p>
                  <h3 className="m-0 text-warning fw-semibold">
                    {executionResult.metadata?.appliedFilters?.length || 0}
                  </h3>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Data Table */}
      <Row className="g-3">
        <Col xs={12}>
          <Card className="shadow-sm">
            <Card.Body className="p-4">
              <Card.Title className="mb-4">
                Dados do Relatório ({data.length} registros exibidos)
              </Card.Title>
              {data.length === 0 ? (
                <div className="text-center py-5">
                  <FontAwesomeIcon
                    icon="inbox"
                    size="3x"
                    className="text-muted mb-3"
                  />
                  <p className="text-muted">
                    Nenhum dado encontrado que corresponda aos critérios do
                    relatório
                  </p>
                </div>
              ) : (
                <div className="border rounded p-3 bg-100">
                  <AdvanceTableProvider {...table}>
                    <div className="mb-3">
                      <AdvanceTableSearchBox />
                    </div>

                    <div className="table-responsive">
                      <AdvanceTable
                        table
                        headerClassName="text-nowrap align-middle"
                        rowClassName="align-middle white-space-nowrap"
                        tableProps={{
                          striped: true,
                          className: 'fs-10 mb-0 overflow-hidden'
                        }}
                      />
                    </div>

                    <div className="mt-3">
                      <Pagination
                        data={executionResult}
                        filters={filters}
                        setFilters={setFilters}
                      />
                    </div>
                  </AdvanceTableProvider>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </CustomReportLayout>
  );
};

export default CustomReportExecution;
